import type { Options } from "highcharts";
import { renderToString } from "react-dom/server";
import styled from "styled-components";

import { tooltipContainerConfig } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { ChangeValue } from "@similarweb/ui-components";
import { Text, Flex } from "@similarweb/ui-components/foundations";

import { percentageSignFilter, yAxisChangeFilter } from "app/filters/ngFilters";

import { NA_VALUE } from "../../../common/utils/utils";

const TOOLTIP_GAP = 16;
const TOOLTIP_DOMAIN_WIDTH = 120;
const TOOLTIP_CELL_WIDTH = 60;
const TOOLTIP_HEADER_MARGIN_BOTTOM = 12;
const TOOLTIP_MARKER_SIZE = 8;
const TOOLTIP_DOMAIN_GAP = 8;

const TooltipContainer = styled(Flex)`
    flex-direction: column;
    gap: 2px;
`;

const TooltipRow = styled(Flex)`
    align-items: center;
    gap: ${TOOLTIP_GAP}px;
`;

const TooltipHeaderRow = styled(TooltipRow)`
    margin-bottom: ${TOOLTIP_HEADER_MARGIN_BOTTOM}px;
`;

const TooltipCell = styled.div<{ width: number }>`
    width: ${({ width }) => width}px;
`;

const TooltipDomainCell = styled(TooltipCell)`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${TOOLTIP_DOMAIN_GAP}px;
`;

const TooltipMarker = styled.span<{ backgroundColor: string }>`
    flex-shrink: 0;
    background: ${({ backgroundColor }) => backgroundColor};
    border-radius: 100%;
    height: ${TOOLTIP_MARKER_SIZE}px;
    width: ${TOOLTIP_MARKER_SIZE}px;
`;

const TooltipDomainText = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    flex: 1;
`;

interface IChartPointOptions {
    domain: string;
    name: string;
    id: string;
    color: string;
    y: number;
    share: number;
    change: number;
    visible: boolean;
}

interface IDonutChartTooltipProps {
    color: string;
    domain: string;
    share: string;
    change: number;
}

const TooltipHeader = ({ children, width }: { children: string; width: number }) => (
    <TooltipCell width={width}>
        <Text
            fontVariant="body2"
            color={colorsPalettes.carbon[400]}
        >
            {children}
        </Text>
    </TooltipCell>
);

const TooltipValue = ({ children }: { children: string | number }) => (
    <Text
        fontVariant="body2"
        color={colorsPalettes.carbon[500]}
    >
        {children}
    </Text>
);

const DonutChartTooltip = ({ color, domain, share, change }: IDonutChartTooltipProps) => (
    <TooltipContainer>
        <TooltipHeaderRow>
            <TooltipHeader width={TOOLTIP_DOMAIN_WIDTH}>Domain</TooltipHeader>
            <TooltipHeader width={TOOLTIP_CELL_WIDTH}>Share</TooltipHeader>
            <TooltipHeader width={TOOLTIP_CELL_WIDTH}>PoP</TooltipHeader>
        </TooltipHeaderRow>
        <TooltipRow>
            <TooltipDomainCell width={TOOLTIP_DOMAIN_WIDTH}>
                <TooltipMarker backgroundColor={color} />
                <TooltipDomainText>
                    <TooltipValue>{domain}</TooltipValue>
                </TooltipDomainText>
            </TooltipDomainCell>
            <TooltipCell width={TOOLTIP_CELL_WIDTH}>
                <TooltipValue>{share}</TooltipValue>
            </TooltipCell>
            <TooltipCell width={TOOLTIP_CELL_WIDTH}>
                <ChangeValue
                    unsigned={change === 0 || change == null}
                    isDecrease={change < 0}
                    value={yAxisChangeFilter()(change, 0) as string}
                />
            </TooltipCell>
        </TooltipRow>
    </TooltipContainer>
);

interface IDonutChartConfigParams {
    chartSize: { height: number; width: number };
}

export const getDonutChartConfig = ({ chartSize }: IDonutChartConfigParams): Partial<Options> => ({
    chart: {
        height: chartSize.height,
        width: chartSize.width,
        spacing: [0, 0, 0, 0] as [number, number, number, number],
    },
    tooltip: {
        enabled: true,
        followPointer: true,
        headerFormat: ``,
        ...tooltipContainerConfig().tooltip,
        useHTML: true,
        pointFormatter() {
            const domain = this.name;
            const color = this.color as string;
            const share = percentageSignFilter()(this.y, 0, NA_VALUE) as string;
            const change = (this.options as IChartPointOptions).change ?? 0;

            const props = {
                domain,
                color,
                share,
                change,
            };

            return renderToString(<DonutChartTooltip {...props} />);
        },
    },
});
